import {ProgressData, ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {error as logError} from 'util/Logging';

export enum DeepResearchToolType {
  // Supervisor-level tools
  ResearchSubAgent = 'ResearchSubAgent',
  Think = 'Think',
  CompleteAnswer = 'CompleteAnswer',
  PublicDataSubAgent = 'PublicDataSubAgent',

  // Sub-agent tools
  SearchTool = 'SearchTool',
  GetWarehouseOverviewTool = 'GetWarehouseOverviewTool',
  SearchNotebookEntries = 'SearchNotebookEntries',
  SearchSequencesTool = 'SearchSequencesTool',
  QueryWarehouseTool = 'QueryWarehouseTool',
  ReadBenchlingDocumentTool = 'ReadBenchlingDocument',
  AttachmentTool = 'AttachmentTool',
  IdsToModelsTool = 'IdsToModelsTool',
  PlottingTool = 'PlottingTool',
  GenerateImageTool = 'GenerateImageTool',
}

/**
 * Human-readable display names for deep research tools when in progress.
 * These are shown while the tool is running (present tense).
 */
export const TOOL_DESCRIPTIONS_IN_PROGRESS = {
  [DeepResearchToolType.ResearchSubAgent]: 'Researching',
  [DeepResearchToolType.Think]: 'Thinking',
  [DeepResearchToolType.CompleteAnswer]: 'Writing report',
  [DeepResearchToolType.PublicDataSubAgent]: 'Searching public data',
  [DeepResearchToolType.SearchTool]: 'Searching Benchling',
  [DeepResearchToolType.GetWarehouseOverviewTool]: 'Getting warehouse overview',
  [DeepResearchToolType.SearchNotebookEntries]: 'Searching notebook entries',
  [DeepResearchToolType.SearchSequencesTool]: 'Searching sequences',
  [DeepResearchToolType.QueryWarehouseTool]: 'Querying warehouse',
  [DeepResearchToolType.ReadBenchlingDocumentTool]: 'Reading notebook',
  [DeepResearchToolType.AttachmentTool]: 'Reading attachment',
  [DeepResearchToolType.IdsToModelsTool]: 'Looking things up',
  [DeepResearchToolType.PlottingTool]: 'Creating chart',
  [DeepResearchToolType.GenerateImageTool]: 'Generating image',
};

/**
 * Human-readable display names for deep research tools when completed.
 * These are shown after the tool has finished running (past tense).
 */
export const TOOL_DESCRIPTIONS_COMPLETED = {
  [DeepResearchToolType.ResearchSubAgent]: 'Researched',
  [DeepResearchToolType.Think]: 'Thought',
  [DeepResearchToolType.CompleteAnswer]: 'Wrote report',
  [DeepResearchToolType.PublicDataSubAgent]: 'Searched public data',
  [DeepResearchToolType.SearchTool]: 'Searched Benchling',
  [DeepResearchToolType.GetWarehouseOverviewTool]: 'Got warehouse overview',
  [DeepResearchToolType.SearchNotebookEntries]: 'Searched notebook entries',
  [DeepResearchToolType.SearchSequencesTool]: 'Searched sequences',
  [DeepResearchToolType.QueryWarehouseTool]: 'Queried warehouse',
  [DeepResearchToolType.ReadBenchlingDocumentTool]: 'Read notebook',
  [DeepResearchToolType.AttachmentTool]: 'Read attachment',
  [DeepResearchToolType.IdsToModelsTool]: 'Looked things up',
  [DeepResearchToolType.PlottingTool]: 'Created chart',
  [DeepResearchToolType.GenerateImageTool]: 'Generated image',
};

/**
 * @deprecated Use TOOL_DESCRIPTIONS_IN_PROGRESS or TOOL_DESCRIPTIONS_COMPLETED instead
 */
export const TOOL_DESCRIPTIONS = TOOL_DESCRIPTIONS_IN_PROGRESS;

export const TOOL_ICONS: Record<DeepResearchToolType, BenchlingIconName> = {
  [DeepResearchToolType.ResearchSubAgent]: 'magic-wand',
  [DeepResearchToolType.Think]: 'lightbulb',
  [DeepResearchToolType.CompleteAnswer]: 'file',
  [DeepResearchToolType.PublicDataSubAgent]: 'link',
  [DeepResearchToolType.SearchTool]: 'magnifying-glass',
  [DeepResearchToolType.GetWarehouseOverviewTool]: 'warehouse-full',
  [DeepResearchToolType.SearchNotebookEntries]: 'magnifying-glass',
  [DeepResearchToolType.SearchSequencesTool]: 'magnifying-glass',
  [DeepResearchToolType.QueryWarehouseTool]: 'warehouse-full',
  [DeepResearchToolType.ReadBenchlingDocumentTool]: 'file',
  [DeepResearchToolType.AttachmentTool]: 'file',
  [DeepResearchToolType.IdsToModelsTool]: 'magnifying-glass',
  [DeepResearchToolType.PlottingTool]: 'chart-bar',
  [DeepResearchToolType.GenerateImageTool]: 'image',
};

/**
 * MCP (Model Context Protocol) tool constants
 *
 * MCP tools follow the naming convention: "server_name__tool_name"
 * For example: "github__list_repos" represents the "list_repos" tool from the "github" server
 *
 * IMPORTANT: Keep MCP_TOOL_NAME_SEPARATOR in sync with the Python constant:
 * benchling/llm/mcp_client_service/constants.py::SERVER_NAME_SEPARATOR
 */
export const MCP_TOOL_NAME_SEPARATOR = '__';

/** Default icon displayed for all MCP tools */
export const MCP_TOOL_DEFAULT_ICON: BenchlingIconName = 'plug';

/** Default description shown when an MCP tool is executing */
export const MCP_TOOL_DEFAULT_DESCRIPTION = 'Using external tool';

/** Icon displayed for unknown/unsupported tool types */
export const UNKNOWN_TOOL_ICON: BenchlingIconName = 'wrench';

/**
 * Check if a tool name is an MCP tool (contains server name prefix)
 */
export function isMCPTool(toolName: string): boolean {
  return toolName.includes(MCP_TOOL_NAME_SEPARATOR);
}

/**
 * Parse MCP tool name into server name and tool name
 */
export function parseMCPToolName(toolName: string): {serverName: string; originalToolName: string} {
  const separatorIndex = toolName.indexOf(MCP_TOOL_NAME_SEPARATOR);
  if (separatorIndex === -1) {
    return {serverName: '', originalToolName: toolName};
  }
  return {
    serverName: toolName.substring(0, separatorIndex),
    originalToolName: toolName.substring(separatorIndex + MCP_TOOL_NAME_SEPARATOR.length),
  };
}

/**
 * Get the display name for a tool (handles both enum and MCP tools)
 */
export function getToolDisplayName(toolName: DeepResearchToolType | string): string {
  if (isMCPTool(toolName)) {
    const {serverName, originalToolName} = parseMCPToolName(toolName);
    return `${serverName}: ${originalToolName}`;
  } else if (TOOL_DESCRIPTIONS.hasOwnProperty(toolName)) {
    return TOOL_DESCRIPTIONS[toolName as DeepResearchToolType];
  }
  // Log unknown tool names to Sentry for monitoring
  logError(`Unknown tool name: ${toolName}`);
  return toolName;
}

/**
 * Get the icon for a tool (handles both enum and MCP tools)
 */
export function getToolIcon(toolName: DeepResearchToolType | string): BenchlingIconName {
  if (isMCPTool(toolName)) {
    return MCP_TOOL_DEFAULT_ICON;
  }
  return TOOL_ICONS[toolName as DeepResearchToolType] || UNKNOWN_TOOL_ICON;
}

/**
 * Base interface for Deep Research tool calls.
 * All specific tool call interfaces extend this.
 */
export interface BaseDeepResearchProgressToolCall extends ProgressToolCall {
  tool: DeepResearchToolType | string;
}

// MCP-specific tool call interface
// TODO (BNCH-140899): include the tool title for user friendly display.
// Hack: Uses template literal type to enable discriminated union narrowing
export interface MCPToolCall extends ProgressToolCall {
  tool: `${string}${typeof MCP_TOOL_NAME_SEPARATOR}${string}`;
  serverName: string;
  originalToolName: string;
  args: Record<string, unknown>;
}

export interface SearchToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.SearchTool;
  query: string;
  objectTypes: Array<string>;
}

export interface GetWarehouseOverviewToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.GetWarehouseOverviewTool;
}

export interface QueryWarehouseToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.QueryWarehouseTool;
  query: string;
}

export interface ReadBenchlingDocumentToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.ReadBenchlingDocumentTool;
  documentId: string;
}

export interface AttachmentToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.AttachmentTool;
  attachmentId: string;
}

export interface PublicDataSubAgentToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.PublicDataSubAgent;
  query: string;
}

export interface SearchNotebookEntriesToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.SearchNotebookEntries;
  queries: Array<string>;
  authoredBy?: Array<string>;
  createdAfter?: string;
  createdBefore?: string;
  modifiedAfter?: string;
  modifiedBefore?: string;
  reviewCompletedAfter?: string;
  reviewCompletedBefore?: string;
  reviewStatus?: string;
  reviewRole?: string;
  inProjectOrFolder?: string;
  inStudy?: string;
  offset: number;
}

export interface SearchSequencesToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.SearchSequencesTool;
  query: string;
}

export interface IdsToModelsToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.IdsToModelsTool;
  objectIds: Array<string>;
  includeRelevantItems: boolean;
}

export interface PlottingToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.PlottingTool;
  code: string;
}

export interface GenerateImageToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.GenerateImageTool;
  prompt: string;
}

// Supervisor-level tool interfaces (no additional properties beyond base)
export interface ThinkToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.Think;
}

export interface ResearchSubAgentToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.ResearchSubAgent;
}

export interface CompleteAnswerToolCall extends BaseDeepResearchProgressToolCall {
  tool: DeepResearchToolType.CompleteAnswer;
}

/**
 * Union of all Deep Research tool call types.
 * Each member has a unique literal `tool` discriminant, enabling TypeScript
 * to narrow the type in switch statements without explicit casts.
 */
export type DeepResearchProgressToolCall =
  | SearchToolCall
  | GetWarehouseOverviewToolCall
  | QueryWarehouseToolCall
  | ReadBenchlingDocumentToolCall
  | SearchNotebookEntriesToolCall
  | SearchSequencesToolCall
  | AttachmentToolCall
  | PublicDataSubAgentToolCall
  | IdsToModelsToolCall
  | PlottingToolCall
  | GenerateImageToolCall
  | ThinkToolCall
  | ResearchSubAgentToolCall
  | CompleteAnswerToolCall
  | MCPToolCall;

/**
 * Deep Research specific progress data.
 * Uses DeepResearchProgressToolCall for proper typing of tool-specific properties.
 */
export interface DeepResearchProgressData extends Omit<ProgressData, 'historyThread'> {
  historyThread: Array<DeepResearchProgressToolCall>;
}
